import { useEffect, useState } from "react";
import axios from "axios";
import { Crown, Star, Fire } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Header, Disclaimer } from "../components/Header";
import { ParlayCard } from "../components/ParlayCard";
import { startCheckout } from "../components/PaywallDialog";
import { toast } from "sonner";

export default function BestValue() {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/best-picks`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        if (err.response?.status === 402) setLocked(true);
        else toast.error("Couldn't load best picks");
      }
    })();
  }, [API]);

  const save = async (parlay) => {
    try {
      await axios.post(`${API}/parlays/save`, { parlay }, { withCredentials: true });
      toast.success("Saved to your history");
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg">
      <Header />
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FFD700] mb-2 flex items-center gap-1.5">
          <Star size={14} weight="fill" /> Refreshed every morning
        </p>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
            Today's best value
          </h1>
          {data?.streak > 0 && (
            <div className="flex items-center gap-2 rounded-full bg-[#FF3366]/10 border border-[#FF3366]/30 px-4 py-2" data-testid="streak-badge">
              <Fire size={20} weight="fill" className="text-[#FF3366]" />
              <span className="text-sm font-black text-[#FF3366]">{data.streak}-day streak</span>
              <span className="text-xs text-slate-400 hidden sm:inline">
                {data.streak >= 7 ? "· locked in" : data.streak >= 3 ? "· heating up" : "· come back tomorrow"}
              </span>
            </div>
          )}
        </div>

        {locked && (
          <div className="text-center py-20 bg-[#141419] border border-[#FFD700]/20 rounded-xl" data-testid="best-picks-locked">
            <Crown size={44} weight="fill" className="text-[#FFD700] mx-auto mb-4" />
            <h2 className="font-display text-3xl font-black uppercase tracking-tight mb-2">Pro members only</h2>
            <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">
              Every morning we scan all four sports and surface the highest +EV parlays of the day.
            </p>
            <button onClick={() => startCheckout(API, "pro_monthly")} data-testid="best-picks-upgrade-btn"
              className="rounded-full bg-[#FFD700] text-black font-bold px-8 py-3 text-sm hover:scale-105 active:scale-95 transition-transform">
              Unlock with Pro — $9.99/mo
            </button>
          </div>
        )}

        {data && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="best-picks-grid">
            {data.picks.map((p, i) => (
              <div key={p.id}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFD700] mb-2">
                  {p.sport.toUpperCase()} · Top value
                </p>
                <ParlayCard parlay={p} index={i} onSave={save} saved={false} />
              </div>
            ))}
          </div>
        )}

        {!data && !locked && <p className="text-slate-500 py-10">Scanning today's slates...</p>}
      </main>
      <Disclaimer />
    </div>
  );
}
